import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Card, CardContent, Menu, MenuItem, Skeleton, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useMemo, useState } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import * as XLSX from 'xlsx'

import { useScoutOsaCount } from '../api/analytics'

interface ChartData {
  name: string
  count: number
  scoutId: string
}

export const ScoutOsaCountChart: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Default to current month
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const date = new Date()
    date.setDate(1) // First day of current month
    return date
  })
  const [toDate, setToDate] = useState<Date | null>(() => {
    const date = new Date()
    date.setMonth(date.getMonth() + 1) // Next month
    date.setDate(0) // Last day of current month
    return date
  })

  // Format dates for API (RFC3339)
  const fromDateStr = fromDate ? fromDate.toISOString() : undefined
  const toDateStr = toDate ? toDate.toISOString() : undefined

  // Fetch data with date filters
  const { data: scoutOsaCountData, isLoading, error } = useScoutOsaCount(fromDateStr, toDateStr)
  const scoutsData = scoutOsaCountData?.data

  const chartData = useMemo<ChartData[]>(() => {
    if (!scoutsData || scoutsData.length === 0) {
      return []
    }

    // Filter scouts with OSA count > 0 and sort by count in descending order
    return scoutsData
      .filter(scout => scout.osaCount > 0)
      .sort((a, b) => b.osaCount - a.osaCount)
      .map(scout => ({
        name: `${scout.firstName} ${scout.lastName}`,
        count: scout.osaCount,
        scoutId: scout.uuid,
      }))
  }, [scoutsData])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (!scoutsData) return

    // Prepare data for export
    const exportData = scoutsData.map(scout => ({
      'Scout Name': `${scout.firstName} ${scout.lastName}`,
      Email: scout.email,
      'OSA Count': scout.osaCount,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Scout OSA Count')

    // Export based on format
    const fileName = `scout-osa-count-${new Date().toISOString().split('T')[0]}.${format}`
    if (format === 'csv') {
      XLSX.writeFile(workbook, fileName, { bookType: 'csv' })
    } else {
      XLSX.writeFile(workbook, fileName)
    }

    handleExportClose()
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography color="error">Error loading scout OSA count data</Typography>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2D3748',
              mb: 3,
            }}
          >
            Top Scouts by Appointment Count (dispatched,completed)
          </Typography>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    )
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2D3748',
              mb: 3,
            }}
          >
            Top Scouts by Appointment Count (dispatched,completed)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 320,
            }}
          >
            <Typography color="text.secondary">No scout data available</Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const maxValue = Math.max(...chartData.map(d => d.count), 10)
  const yAxisMax = Math.ceil(maxValue / 5) * 5 // Round up to nearest 5

  // Calculate dynamic width based on number of scouts (minimum 60px per bar)
  const barWidth = 60
  const chartWidth = Math.max(800, chartData.length * barWidth)
  const chartHeight = 320

  return (
    <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#2D3748',
                mb: 2,
              }}
            >
              Top Scouts by Appointment Count (dispatched,completed)
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {chartData.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: chartHeight,
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Box sx={{ width: chartWidth, height: chartHeight, minWidth: 800 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 80,
                }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#718096', fontSize: 11 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis
                  tick={{ fill: '#718096' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  domain={[0, yAxisMax]}
                  ticks={Array.from({ length: 5 }, (_, i) => Math.round((yAxisMax / 4) * i))}
                  label={{
                    value: 'Number of Appointments',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#718096' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#2D3748', fontWeight: 600 }}
                  itemStyle={{ color: '#48BB78' }}
                  formatter={(value: number) => [`${value} appointments`, 'Total']}
                />
                <Bar dataKey="count" fill="#48BB78" radius={[4, 4, 0, 0]} maxBarSize={60}>
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default ScoutOsaCountChart
